import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatPHP, formatDate, formatDateTime } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import StatusBadge from "@/components/app/StatusBadge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function MyApprovalList({ endpoint, fields, kind }) {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({});
    const [medcert, setMedcert] = useState(null);
    const load = async () => { const r = await api.get(endpoint); setItems(r.data.items || []); };
    useEffect(() => { load(); }, [endpoint]);
    const isSick = kind === "leave" && form.leave_type === "sick";
    const submit = async () => {
        if (isSick && !medcert) return toast.error("A medical certificate is required for sick leave.");
        try {
            const { data: created } = await api.post(endpoint, form);
            if (isSick && medcert && created?.id) {
                const fd = new FormData();
                fd.append("file", medcert);
                await api.post(`/me/leaves/${created.id}/medical-certificate`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            }
            toast.success(isSick ? "Sick leave filed with medical certificate" : "Submitted");
            setOpen(false); setForm({}); setMedcert(null); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    return (
        <>
            <div className="flex justify-end mb-2">
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm flex items-center gap-2" data-testid={`new-my-${kind}`}><Plus className="size-4" /> New {kind}</button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>File {kind}</DialogTitle></DialogHeader>
                        <div className="space-y-2">
                            {fields.map((f) => (
                                <div key={f.key}><Label>{f.label}</Label>
                                    {f.type === "select" ? (
                                        <select className="w-full h-10 px-3 rounded-md border border-border" value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}>
                                            <option value="">—</option>
                                            {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                    ) : (
                                        <Input type={f.type || "text"} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value })} data-testid={`my-${kind}-${f.key}`} />
                                    )}
                                </div>
                            ))}
                            {isSick && (
                                <div className="border border-amber-300 bg-amber-50 rounded-md p-2.5" data-testid="medcert-box">
                                    <div className="text-xs font-semibold text-amber-900">🩺 Medical certificate — REQUIRED for sick leave</div>
                                    <div className="text-[11px] text-amber-800 mt-0.5 mb-1.5">
                                        The certificate must be issued by a <b>company-accredited clinic</b>.
                                        Certificates from non-accredited providers will not be honored.
                                    </div>
                                    <input type="file" accept=".pdf,image/*" onChange={(e) => setMedcert(e.target.files?.[0] || null)} className="w-full text-xs" data-testid="medcert-file" />
                                    {medcert && <div className="text-[11px] text-emerald-700 mt-1">✓ {medcert.name}</div>}
                                </div>
                            )}
                        </div>
                        <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={submit} data-testid={`submit-my-${kind}`}>Submit</button></DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
            <TableWrap>
                <thead><tr>{fields.map((f) => <th key={f.key}>{f.label}</th>)}<th>Status</th><th>Filed</th></tr></thead>
                <tbody>
                    {items.map((it) => (
                        <tr key={it.id} data-row-id={it.id} data-testid={`my-${kind}-${it.id}`}>
                            {fields.map((f) => <td key={f.key}>{it[f.key] ?? "—"}</td>)}
                            <td><StatusBadge value={it.status} /></td>
                            <td className="font-mono text-xs">{formatDate(it.created_at)}</td>
                        </tr>
                    ))}
                    {!items.length && <tr><td colSpan={fields.length + 2}><Empty title={`No ${kind} requests yet`} /></td></tr>}
                </tbody>
            </TableWrap>
        </>
    );
}

/* Time & schedule adjustment requests — corrections flow into the real
 * attendance record; shift changes re-assign the employee's individual shift. */
function AdjustmentsTab() {
    const [mine, setMine] = useState({ time_adjustments: [], schedule_adjustments: [] });
    const [shiftInfo, setShiftInfo] = useState(null);
    const [timeOpen, setTimeOpen] = useState(false);
    const [schedOpen, setSchedOpen] = useState(false);
    const [tf, setTf] = useState({ work_date: "", requested_time_in: "", requested_time_out: "", reason: "" });
    const [currentAtt, setCurrentAtt] = useState(null);
    const [sf, setSf] = useState({ requested_shift_id: "", effective_date: "", reason: "" });

    const load = () => {
        api.get("/adjustments/mine").then((r) => setMine(r.data)).catch(() => {});
        api.get("/adjustments/shift-options").then((r) => setShiftInfo(r.data)).catch(() => {});
    };
    useEffect(() => { load(); }, []);

    // show the recorded times for the chosen date so the correction has context
    useEffect(() => {
        if (!tf.work_date) return setCurrentAtt(null);
        api.get("/me/attendance").then((r) => {
            setCurrentAtt((r.data.items || []).find((a) => a.work_date === tf.work_date) || false);
        }).catch(() => setCurrentAtt(null));
    }, [tf.work_date]);

    const hhmm = (iso) => iso ? new Date(iso).toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Manila" }) : "—";

    const submitTime = async () => {
        try {
            await api.post("/adjustments/time", tf);
            toast.success("Time adjustment submitted for approval");
            setTimeOpen(false); setTf({ work_date: "", requested_time_in: "", requested_time_out: "", reason: "" });
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const submitSched = async () => {
        try {
            await api.post("/adjustments/schedule", sf);
            toast.success("Shift change request submitted for approval");
            setSchedOpen(false); setSf({ requested_shift_id: "", effective_date: "", reason: "" });
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const SB = { submitted: "badge badge-pending", approved: "badge badge-approved", rejected: "badge badge-destructive" };

    return (
        <div className="space-y-4">
            <Surface className="p-3 flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm">
                    <b>My shift:</b> {shiftInfo?.current_shift ? `${shiftInfo.current_shift.name} (${shiftInfo.current_shift.start_time}–${shiftInfo.current_shift.end_time}, grace ${shiftInfo.current_shift.grace_minutes}m)` : "no shift assigned"}
                </div>
                <div className="flex gap-2">
                    <button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={() => setTimeOpen(true)} data-testid="new-time-adj">🕐 Request time adjustment</button>
                    <button className="h-9 px-3 rounded-md border border-primary text-primary text-sm" onClick={() => setSchedOpen(true)} data-testid="new-sched-adj">📅 Request shift change</button>
                </div>
            </Surface>

            <div>
                <div className="text-sm font-semibold mb-1">Time adjustment requests</div>
                <TableWrap>
                    <thead><tr><th>Date</th><th>Recorded</th><th>Requested</th><th>Reason</th><th>Status</th></tr></thead>
                    <tbody>
                        {mine.time_adjustments.map((a) => (
                            <tr key={a.id} data-row-id={a.id}>
                                <td className="font-mono text-xs">{a.work_date}</td>
                                <td className="font-mono text-xs">{hhmm(a.current_time_in)}–{hhmm(a.current_time_out)}</td>
                                <td className="font-mono text-xs font-semibold">{a.requested_time_in || "—"}–{a.requested_time_out || "—"}</td>
                                <td className="text-xs">{a.reason}</td>
                                <td><span className={SB[a.status]}>{a.status}</span>{a.decision_note && <span className="block text-[10px] text-muted-foreground">“{a.decision_note}”</span>}</td>
                            </tr>
                        ))}
                        {!mine.time_adjustments.length && <tr><td colSpan={5}><Empty title="No time adjustments yet" /></td></tr>}
                    </tbody>
                </TableWrap>
            </div>

            <div>
                <div className="text-sm font-semibold mb-1">Shift change requests</div>
                <TableWrap>
                    <thead><tr><th>From</th><th>To</th><th>Effective</th><th>Reason</th><th>Status</th></tr></thead>
                    <tbody>
                        {mine.schedule_adjustments.map((a) => (
                            <tr key={a.id} data-row-id={a.id}>
                                <td className="text-xs">{a.current_shift_name || "—"}</td>
                                <td className="text-xs font-semibold">{a.requested_shift_name} <span className="text-muted-foreground">({a.requested_shift_hours})</span></td>
                                <td className="font-mono text-xs">{a.effective_date}{a.applied && <span className="text-emerald-600 block text-[10px]">applied ✓</span>}</td>
                                <td className="text-xs">{a.reason}</td>
                                <td><span className={SB[a.status]}>{a.status}</span></td>
                            </tr>
                        ))}
                        {!mine.schedule_adjustments.length && <tr><td colSpan={5}><Empty title="No shift change requests yet" /></td></tr>}
                    </tbody>
                </TableWrap>
            </div>

            {/* Time adjustment dialog */}
            <Dialog open={timeOpen} onOpenChange={setTimeOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Request time adjustment</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        <div><Label>Date to correct</Label><Input type="date" value={tf.work_date} onChange={(e) => setTf({ ...tf, work_date: e.target.value })} data-testid="tadj-date" /></div>
                        {tf.work_date && (
                            <div className="text-xs rounded-md border border-border bg-secondary/50 p-2">
                                {currentAtt === false ? "No attendance record on this date — the approved times will create one."
                                    : currentAtt ? <>Recorded: <b className="font-mono">{hhmm(currentAtt.time_in)}–{hhmm(currentAtt.time_out)}</b> ({currentAtt.hours_worked || 0}h{currentAtt.late_minutes ? `, late ${currentAtt.late_minutes}m` : ""})</>
                                    : "Checking the record…"}
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label>Corrected time in</Label><Input type="time" value={tf.requested_time_in} onChange={(e) => setTf({ ...tf, requested_time_in: e.target.value })} data-testid="tadj-in" /></div>
                            <div><Label>Corrected time out</Label><Input type="time" value={tf.requested_time_out} onChange={(e) => setTf({ ...tf, requested_time_out: e.target.value })} data-testid="tadj-out" /></div>
                        </div>
                        <div><Label>Reason</Label><Input value={tf.reason} onChange={(e) => setTf({ ...tf, reason: e.target.value })} placeholder="Forgot to clock out — left at 5:30 PM" data-testid="tadj-reason" /></div>
                        <div className="text-[11px] text-muted-foreground">On approval the correction is written into your attendance record and lateness is re-checked against your shift ({shiftInfo?.current_shift?.start_time || "—"} + grace).</div>
                    </div>
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={submitTime} data-testid="tadj-submit">Submit</button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Shift change dialog */}
            <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Request shift change</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        <div className="text-xs rounded-md border border-border bg-secondary/50 p-2">
                            Current: <b>{shiftInfo?.current_shift ? `${shiftInfo.current_shift.name} (${shiftInfo.current_shift.start_time}–${shiftInfo.current_shift.end_time})` : "no shift assigned"}</b>
                        </div>
                        <div>
                            <Label>Move to shift</Label>
                            <select className="w-full h-10 px-3 rounded-md border border-border" value={sf.requested_shift_id} onChange={(e) => setSf({ ...sf, requested_shift_id: e.target.value })} data-testid="sadj-shift">
                                <option value="">Pick a shift…</option>
                                {(shiftInfo?.shifts || []).filter((s) => s.id !== shiftInfo?.current_shift?.id).map((s) => (
                                    <option key={s.id} value={s.id}>{s.name} · {s.start_time}–{s.end_time}</option>
                                ))}
                            </select>
                        </div>
                        <div><Label>Effective date</Label><Input type="date" value={sf.effective_date} onChange={(e) => setSf({ ...sf, effective_date: e.target.value })} data-testid="sadj-date" /></div>
                        <div><Label>Reason</Label><Input value={sf.reason} onChange={(e) => setSf({ ...sf, reason: e.target.value })} placeholder="Enrolled in evening classes starting August" data-testid="sadj-reason" /></div>
                        <div className="text-[11px] text-muted-foreground">On approval your assigned shift changes on the effective date — clock-in lateness follows the new schedule automatically.</div>
                    </div>
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={submitSched} data-testid="sadj-submit">Submit</button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const NICE_LEAVE = {
    service_incentive_leave: "Service Incentive Leave",
    maternity: "Maternity",
    paternity: "Paternity",
    solo_parent: "Solo Parent",
    special_leave_women: "Special Leave (Women)",
    vawc: "VAWC",
    leave_without_pay: "Leave without pay (deducted from pay)",
};

export default function MyTime() {
    const [params] = useSearchParams();
    const [att, setAtt] = useState([]);
    const [balances, setBalances] = useState(null);
    const [ledger, setLedger] = useState(null);
    useEffect(() => {
        api.get("/me/attendance").then((r) => setAtt(r.data.items || []));
        api.get("/me/leave-balances").then((r) => setBalances(r.data)).catch(() => setBalances(null));
        api.get("/me/leave-ledger").then((r) => setLedger(r.data)).catch(() => setLedger(null));
    }, []);
    return (
        <div>
            <PageHeader title="My Time" subtitle="File your leave & overtime, request time corrections and shift changes, view attendance and balances." />
            <Tabs defaultValue={params.get("tab") === "adjustments" ? "adjustments" : "leaves"}>
                <TabsList>
                    <TabsTrigger value="leaves" data-testid="tab-my-leaves">Leaves</TabsTrigger>
                    <TabsTrigger value="balances" data-testid="tab-my-balances">Balances</TabsTrigger>
                    <TabsTrigger value="overtime" data-testid="tab-my-overtime">Overtime</TabsTrigger>
                    <TabsTrigger value="attendance" data-testid="tab-my-attendance">Attendance</TabsTrigger>
                    <TabsTrigger value="adjustments" data-testid="tab-my-adjustments">Adjustments</TabsTrigger>
                </TabsList>
                <TabsContent value="adjustments"><AdjustmentsTab /></TabsContent>
                <TabsContent value="leaves">
                    <MyApprovalList endpoint="/me/leaves" kind="leave" fields={[
                        { key: "leave_type", label: "Type", type: "select",
                          options: [...(balances?.balances || []).filter((b) => b.applicable).map((b) => b.leave_type), "leave_without_pay"] },
                        { key: "start_date", label: "From", type: "date" },
                        { key: "end_date", label: "To", type: "date" },
                        { key: "days", label: "Days", type: "number" },
                        { key: "reason", label: "Reason" },
                    ]} />
                </TabsContent>
                <TabsContent value="balances">
                    <div className="text-sm font-semibold mb-1">Company Leave (accruing)</div>
                    <TableWrap>
                        <thead><tr><th>Leave</th><th className="num">Accrued</th><th className="num">Carried Over</th><th className="num">Used</th><th className="num">Remaining</th><th className="num">Cash Value</th></tr></thead>
                        <tbody>
                            {(ledger?.balances || []).map((b) => (
                                <tr key={b.leave_type} data-testid={`ledger-${b.leave_type}`}>
                                    <td>{b.name}</td>
                                    <td className="num">{b.accrued_days}</td>
                                    <td className="num">{b.carryover_in}</td>
                                    <td className="num">{b.used_days}</td>
                                    <td className="num font-semibold">{b.remaining_days}</td>
                                    <td className="num">{b.encashable_value != null ? formatPHP(b.encashable_value) : "—"}</td>
                                </tr>
                            ))}
                            {ledger && !ledger.balances.length && <tr><td colSpan={6}><Empty title="No company leave policies configured" /></td></tr>}
                            {!ledger && <tr><td colSpan={6}><Empty title="Company leave balances unavailable" /></td></tr>}
                        </tbody>
                    </TableWrap>

                    <div className="text-sm font-semibold mt-5 mb-1">Statutory Leave</div>
                    {balances && (
                        <div className="text-xs text-muted-foreground mb-2">
                            Year {balances.year} · {balances.years_of_service} yrs of service. Only statutory leaves that apply to you are shown.
                        </div>
                    )}
                    <TableWrap>
                        <thead><tr><th>Statutory Leave</th><th className="num">Entitled</th><th className="num">Used</th><th className="num">Remaining</th><th className="num">Cash Value</th></tr></thead>
                        <tbody>
                            {(balances?.balances || []).filter((b) => b.applicable).map((b) => (
                                <tr key={b.leave_type} data-testid={`balance-${b.leave_type}`}>
                                    <td>{NICE_LEAVE[b.leave_type] || b.leave_type}</td>
                                    <td className="num">{b.entitlement_days}</td>
                                    <td className="num">{b.used_days}</td>
                                    <td className="num font-semibold">{b.remaining_days}</td>
                                    <td className="num">{b.encashable_value != null ? formatPHP(b.encashable_value) : "—"}</td>
                                </tr>
                            ))}
                            {balances && !balances.balances.some((b) => b.applicable) && <tr><td colSpan={5}><Empty title="No statutory leave applies yet" subtitle="Service Incentive Leave starts after 1 year of service." /></td></tr>}
                            {!balances && <tr><td colSpan={5}><Empty title="Leave balances unavailable" /></td></tr>}
                        </tbody>
                    </TableWrap>
                </TabsContent>
                <TabsContent value="overtime">
                    <MyApprovalList endpoint="/me/overtime" kind="overtime" fields={[
                        { key: "work_date", label: "Date", type: "date" },
                        { key: "hours", label: "Hours", type: "number" },
                        { key: "reason", label: "Reason" },
                    ]} />
                </TabsContent>
                <TabsContent value="attendance">
                    <TableWrap>
                        <thead><tr><th>Date</th><th>Time In</th><th>Time Out</th><th className="num">Break (min)</th><th className="num">Hours</th><th>Where</th><th>Geofence</th></tr></thead>
                        <tbody>
                            {att.map((a) => (
                                <tr key={a.id} data-row-id={a.id}>
                                    <td className="font-mono">{a.work_date}</td>
                                    <td className="font-mono text-xs">{a.time_in ? formatDateTime(a.time_in) : "—"}</td>
                                    <td className="font-mono text-xs">{a.time_out ? formatDateTime(a.time_out) : "—"}</td>
                                    <td className="num">{a.break_source === "recorded" ? a.break_minutes : "—"}</td>
                                    <td className="num">{(a.hours_worked || 0).toFixed(2)}</td>
                                    <td className="text-xs">{a.work_from_home ? "Home" : "—"}</td>
                                    <td>{a.geofence_result ? <StatusBadge value={a.geofence_result} /> : "—"}</td>
                                </tr>
                            ))}
                            {!att.length && <tr><td colSpan={7}><Empty title="No attendance" /></td></tr>}
                        </tbody>
                    </TableWrap>
                </TabsContent>
            </Tabs>
        </div>
    );
}
